/**
 * Transcript Version Orbs
 *
 * Component for displaying and selecting transcript revisions.
 * Similar to the version orbs in TTMLRankingEnhanced.tsx.
 */

import React, { useState, useRef, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { TranscriptRevision } from '../services/transcriptRefinementService';

interface TranscriptVersionOrbsProps {
  revisions: TranscriptRevision[];
  selectedChecksum: string | null;
  onSelectRevision: (checksum: string) => void;
  onViewDiff?: (checksum: string) => void;
  onDeleteRevision?: (checksum: string) => void;
  compact?: boolean;
  canDelete?: boolean;
}

export const TranscriptVersionOrbs: React.FC<TranscriptVersionOrbsProps> = ({
  revisions,
  selectedChecksum,
  onSelectRevision,
  onViewDiff,
  onDeleteRevision,
  compact = false,
  canDelete = false
}) => {
  const [showDeleteFor, setShowDeleteFor] = useState<string | null>(null);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = useCallback((checksum: string, canDeleteThis: boolean) => {
    if (!canDelete || !canDeleteThis || !onDeleteRevision) return;

    // Start a timer to show delete button after 1.5 seconds
    hoverTimerRef.current = setTimeout(() => {
      setShowDeleteFor(checksum);
    }, 1500);
  }, [canDelete, onDeleteRevision]);

  const handleMouseLeave = useCallback(() => {
    // Clear timer and hide delete button
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setShowDeleteFor(null);
  }, []);

  const handleDelete = useCallback((e: React.MouseEvent, checksum: string) => {
    e.stopPropagation();
    if (onDeleteRevision) {
      onDeleteRevision(checksum);
    }
    setShowDeleteFor(null);
  }, [onDeleteRevision]);

  if (revisions.length === 0) {
    return null;
  }

  // Sort revisions by version number
  const sortedRevisions = [...revisions].sort((a, b) => a.version - b.version);

  return (
    <div className={`flex items-center gap-1 ${compact ? 'flex-wrap' : ''}`}>
      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
        v{sortedRevisions.length > 0 ? sortedRevisions[sortedRevisions.length - 1].version : 1}
      </span>
      {sortedRevisions.map((revision) => {
        const isSelected = revision.checksum === selectedChecksum;
        const hasParent = revision.parent_checksum !== null;
        // Can only delete if: not the only revision, and no other revisions have this as parent
        const hasChildren = sortedRevisions.some(r => r.parent_checksum === revision.checksum);
        const canDeleteThis = sortedRevisions.length > 1 && !hasChildren;
        const showingDelete = showDeleteFor === revision.checksum;

        return (
          <div
            key={revision.checksum}
            className="relative group"
            onMouseEnter={() => handleMouseEnter(revision.checksum, canDeleteThis)}
            onMouseLeave={handleMouseLeave}
          >
            <button
              onClick={() => onSelectRevision(revision.checksum)}
              onContextMenu={(e) => {
                e.preventDefault();
                if (onViewDiff && hasParent) {
                  onViewDiff(revision.checksum);
                }
              }}
              className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center text-[9px] font-medium ${
                isSelected
                  ? 'bg-blue-500 border-blue-500 text-white'
                  : hasParent
                    ? 'border-purple-400 dark:border-purple-500 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                    : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              title={`Version ${revision.version}${revision.revision_notes ? `: ${revision.revision_notes.substring(0, 100)}...` : ''}`}
            >
              {revision.version}
            </button>

            {/* Delete button - appears after hovering for 1.5 seconds */}
            {showingDelete && canDelete && canDeleteThis && (
              <button
                onClick={(e) => handleDelete(e, revision.checksum)}
                className="absolute -top-6 left-1/2 transform -translate-x-1/2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-all z-50"
                title="Delete this revision"
              >
                <Trash2 size={12} />
              </button>
            )}

            {/* Tooltip on hover */}
            <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded transition-opacity pointer-events-none whitespace-nowrap z-50 ${showingDelete ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`}>
              <div>Version {revision.version}</div>
              {revision.model && (
                <div className="text-gray-400">{revision.model}</div>
              )}
              {revision.cost_usd !== null && revision.cost_usd > 0 && (
                <div className="text-gray-400">${revision.cost_usd.toFixed(2)}</div>
              )}
              {revision.revision_notes && (
                <div className="text-gray-400 max-w-[200px] truncate">
                  {revision.revision_notes}
                </div>
              )}
              {canDelete && canDeleteThis && (
                <div className="text-gray-500 text-[10px] mt-1">Hold to delete</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TranscriptVersionOrbs;
